/**
 * PostHog Custom Events for Shopify
 * ---------------------------------
 * 
 * This file sets up custom event tracking for key user interactions throughout the store.
 * All events automatically include common context like URL, path, referrer, Shopify meta data, and A/B test variants.
 * 
 * CUSTOM EVENTS TRACKED:
 * 
 * 1. PRODUCT DISCOVERY EVENTS:
 *    - pdp_recommendation_click: User clicks on a recommended product
 *      Properties: product_handle, source (default or custom), position
 * 
 *    - featured_collection_product_click: User clicks on a product in a featured collection
 *      Properties: product_handle, featured_id, position
 * 
 *    - collection_product_click: User clicks on a product in a collection grid
 *      Properties: product_handle, position
 * 
 *    - search_result_click: User clicks on a search result
 *      Properties: result_url, result_title, result_type (product/collection/other), query, position
 * 
 * 2. PRODUCT DETAIL EVENTS:
 *    - pdp_add_to_cart_click: User clicks add to cart button on product page
 *      Properties: variant_id, quantity
 * 
 *    - pdp_quantity_change: User changes quantity input on product page
 *      Properties: quantity
 * 
 *    - pdp_info_tab_toggle: User expands/collapses product info sections
 *      Properties: section (sustainability/shipping/returns), expanded (true/false), toggle_text
 * 
 * 3. NAVIGATION & ENGAGEMENT EVENTS:
 *    - menu_item_click: User clicks on navigation menu item
 *      Properties: menu_label, menu_href
 * 
 *    - homepage_banner_click: User clicks on a homepage banner or CTA
 *      Properties: banner_id, banner_name, cta_label, cta_href
 * 
 *    - searchbar_click: User focuses on search input
 *      Properties: placement (header/unknown)
 * 
 *    - searchbar_input: User types into the search input (debounced)
 *      Properties: search_text_value (search query), placement (header/unknown)
 * 
 *    - searchbar_clear: User clears the search input (debounced)
 *      Properties: placement (header/unknown)
 * 
 *    - searchbar_submit: User submits a search form (Enter or search button)
 *      Properties: search_text_value (search query), placement (header/unknown)
 * 
 * EVENT ARCHITECTURE:
 * - Uses a single delegated click handler for performance
 * - Adds consistent context to all events via common() function
 * - Implements debouncing for high-frequency events
 * - Handles errors gracefully with try/catch blocks
 * - Waits for PostHog to be available before initializing
 */
(() => {
  "use strict";

  // --- Configuration ---
  const CONFIG = {
    debugMode: false, // Set to true to see console logs in the browser
    retryDelay: 1000, // How long to wait before retrying to find PostHog
    maxRetries: 10    // How many times to retry before giving up
  };

  // --- Core Functions ---

  const log = (...args) => {
    if (CONFIG.debugMode) {
      console.log('[PostHog Custom Events]', ...args);
    }
  };

  const capture = (name, props = {}) => {
    if (window.posthog && window.posthog.capture) {
      const commonProps = common();
      window.posthog.capture(name, { ...commonProps, ...props });
      log(`Event captured: ${name}`, { ...commonProps, ...props });
    }
  };

  const debounce = (fn, ms = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  // --- Helpers ---

  const getText = (el) => (el?.textContent || "").trim().slice(0, 200);
  const getAttr = (el, name) => el?.getAttribute?.(name) || null;
  const getHref = (el) => (el?.closest('a')?.href || el?.href || null);

  const getProductHandleFromHref = (href) => {
    try {
      const u = new URL(href, location.origin);
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("products");
      return idx !== -1 && parts[idx + 1] ? parts[idx + 1] : null;
    } catch {
      return null;
    }
  };

  const getIndexWithin = (el, selectorWithin) => {
    try {
      const parent = el.closest(selectorWithin);
      if (!parent) return null;
      const items = Array.from(parent.querySelectorAll('a, button, [role="link"]'));
      const i = items.findIndex(n => n === el || n.contains(el));
      return i !== -1 ? i : null;
    } catch {
      return null;
    }
  };

  // --- Context Providers ---

  const getShopifyMetaContext = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const ctx = {};
    try {
      ctx.shop_currency = window.Shopify?.currency?.active || window.Shopify?.currency || null;
      ctx.theme_id = window.Shopify?.theme?.id || window.theme?.id || null;
      ctx.cart_token = window.Shopify?.cartToken || null;

      const collectionIdx = pathParts.indexOf("collections");
      ctx.collection_handle = collectionIdx !== -1 && pathParts[collectionIdx + 1] ? pathParts[collectionIdx + 1] : null;

      const productIdx = pathParts.indexOf("products");
      const urlHandle = productIdx !== -1 && pathParts[productIdx + 1] ? pathParts[productIdx + 1] : null;
      const metaOgUrl = document.querySelector('meta[property="og:url"]');
      const metaHandle = metaOgUrl ? getProductHandleFromHref(metaOgUrl.content) : null;
      ctx.product_handle = urlHandle || metaHandle || null;
    } catch (e) {
      log('Error getting Shopify context:', e);
    }
    return ctx;
  };

  const getAbTestContext = () => {
    const ctx = {};
    try {
      if (window.INITIAL_AB_VARIANT !== undefined) {
        ctx.ab_variant_percentage = window.INITIAL_AB_VARIANT;
      }
      const campaignMatch = document.cookie.match(/campaign_id=([^;]+)/);
      if (campaignMatch && campaignMatch[1]) {
        ctx.campaign_id = campaignMatch[1];
      }
    } catch (e) {
      log('Error getting A/B test context:', e);
    }
    return ctx;
  };

  // This function runs for EVERY event to add consistent, valuable context.
  const common = () => ({
    url: window.location.href,
    path: window.location.pathname,
    referrer: document.referrer || null,
    ...getShopifyMetaContext(),
    ...getAbTestContext()
  });

  // --- Event Handlers ---

  function setupEventListeners() {
    log('Setting up event listeners...');

    // A single, delegated click listener for performance.
    // The order of checks is important: more specific selectors should come first.
    document.addEventListener('click', (e) => {
      const target = e.target;

      // PDP Recommendation Click
      const recLink = target.closest('[data-section-type*="recommendations"] a[href*="/products/"], [data-recommendations] a[href*="/products/"]');
      if (recLink) {
        const getRecSource = (href) => {
          try {
            const u = new URL(href, location.origin);
            const custom = u.searchParams.get('rec') || u.searchParams.get('recommendation_source');
            return custom ? `custom:${custom}` : 'default';
          } catch { return 'unknown'; }
        };
        capture('pdp_recommendation_click', {
          product_handle: getProductHandleFromHref(recLink.href),
          source: getRecSource(recLink.href),
          position: getIndexWithin(recLink, '[data-section-type*="recommendations"], [data-recommendations]')
        });
        return;
      }

      // Featured Collection Product Click
      const featuredProduct = target.closest('[data-featured-collection] a[href*="/products/"]');
      if (featuredProduct) {
        capture('featured_collection_product_click', {
          product_handle: getProductHandleFromHref(featuredProduct.href),
          featured_id: getAttr(featuredProduct.closest('[data-featured-collection]'), 'data-featured-collection'),
          position: getIndexWithin(featuredProduct, '[data-featured-collection]')
        });
        return;
      }

      // Collection Grid Product Click
      const collectionProduct = target.closest('.collection a[href*="/products/"], [data-collection-grid] a[href*="/products/"]');
      if (collectionProduct) {
        capture('collection_product_click', {
          product_handle: getProductHandleFromHref(collectionProduct.href),
          position: getIndexWithin(collectionProduct, '.collection, [data-collection-grid]')
        });
        return;
      }

      // Predictive Search Result Click
      const searchResult = target.closest('[data-predictive-search] a, .predictive-search a');
      if (searchResult) {
        const href = searchResult.href;
        if (href) {
          let query = null;
          try { const u = new URL(href, location.origin); query = u.searchParams.get('q'); } catch {}
          capture('search_result_click', {
            result_url: href,
            result_title: getText(searchResult),
            result_type: href.includes('/products/') ? 'product' : href.includes('/collections/') ? 'collection' : 'other',
            query,
            position: getIndexWithin(searchResult, '[data-predictive-search], .predictive-search')
          });
        }
        return;
      }

      // Add to Cart Click
      const atcButton = target.closest('form[action*="/cart/add"] [type="submit"], button[name="add"], [data-add-to-cart]');
      if (atcButton) {
        const form = atcButton.closest('form[action*="/cart/add"]');
        capture('pdp_add_to_cart_click', {
          variant_id: form?.querySelector('[name="id"]')?.value || null,
          quantity: parseInt(form?.querySelector('[name="quantity"]')?.value, 10) || 1
        });
        return;
      }

      // Homepage Banner Click
      const bannerButton = target.closest('.banner a, .banner button, [data-banner] a, [data-banner] button');
      if (bannerButton) {
        const banner = bannerButton.closest('[data-banner], .banner');
        capture('homepage_banner_click', {
          banner_id: getAttr(banner, 'data-banner-id'),
          banner_name: getAttr(banner, 'data-banner-name'),
          cta_label: getText(bannerButton),
          cta_href: getHref(bannerButton)
        });
        return;
      }

      // Menu Item Click
      const menuItem = target.closest('header nav a, [data-menu] a, nav a');
      if (menuItem) {
        capture('menu_item_click', {
          menu_label: getAttr(menuItem, 'data-menu-item') || getText(menuItem),
          menu_href: menuItem.href
        });
        return;
      }

      // PDP Info Tab/Accordion Toggle
      const infoToggle = target.closest('[data-accordion], .accordion__title, button[aria-controls], [data-details-toggle]');
      if (infoToggle && common().product_handle) { // Only fire on PDPs
        const labelAttr = (getAttr(infoToggle, 'data-accordion') || '').toLowerCase();
        const labelText = getText(infoToggle).toLowerCase();
        let section = null;
        if (['sustainability', 'shipping', 'returns'].includes(labelAttr)) section = labelAttr;
        else if (labelText.includes('sustain')) section = 'sustainability';
        else if (labelText.includes('ship')) section = 'shipping';
        else if (labelText.includes('return')) section = 'returns';

        if (section) {
          const isExpanded = (getAttr(infoToggle, 'aria-expanded') || '').toLowerCase() === 'true';
          capture('pdp_info_tab_toggle', {
            section,
            expanded: !isExpanded, // We capture the state *after* the click
            toggle_text: getText(infoToggle)
          });
        }
        return;
      }
    });

    // Listeners for non-click events
    document.addEventListener('focusin', (e) => {
      if (e.target.matches('input[type="search"], input[name="q"], form[action*="/search"] input[type="text"]')) {
        capture('searchbar_click', {
          placement: getAttr(e.target, 'data-searchbar') || (e.target.closest('header') ? 'header' : 'unknown'),
        });
      }
    });

    // PDP quantity changes (debounced at 300ms)
    document.addEventListener('input', debounce((e) => {
      if (e.target.matches('input[name="quantity"], input[type="number"][name*="quantity"]') && common().product_handle) {
        const quantity = parseInt(e.target.value, 10);
        if (!isNaN(quantity)) {
          capture('pdp_quantity_change', { quantity });
        }
      }
    }, 300));

    // Search input (debounced at 1000ms)
    document.addEventListener('input', debounce((e) => {
      if (e.target.matches('input[type="search"], input[name="q"], form[action*="/search"] input[type="text"]')) {
        const searchValue = e.target.value?.trim();
        if (searchValue && searchValue.length > 0) {
          capture('searchbar_input', {
            search_text_value: searchValue,
            placement: getAttr(e.target, 'data-searchbar') || (e.target.closest('header') ? 'header' : 'unknown'),
          });
        }
      }
    }, 1000));

    // Search input clear (debounced at 500ms)
    document.addEventListener('input', debounce((e) => {
      if (
        e.target.matches('input[type="search"], input[name="q"], form[action*="/search"] input[type="text"]') &&
        (!e.target.value || e.target.value.trim().length === 0)
      ) {
        capture('searchbar_clear', {
          placement: getAttr(e.target, 'data-searchbar') || (e.target.closest('header') ? 'header' : 'unknown'),
        });
      }
    }, 500));

    // Track search form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.matches('form[action*="/search"]')) {
        const input = form.querySelector('input[type="search"], input[name="q"], input[type="text"]');
        const searchValue = input?.value?.trim();
        if (searchValue && searchValue.length > 0) {
          capture('searchbar_submit', {
            search_text_value: searchValue,
            placement: getAttr(input, 'data-searchbar') || (input.closest('header') ? 'header' : 'unknown'),
          });
        }
      }
    });
  }

  // --- Initialization ---

  function init() {
    let retryCount = 0;
    const checkPostHog = () => {
      if (typeof window.posthog?.capture === 'function') {
        log('PostHog found. Initializing custom event tracking.');
        setupEventListeners();
      } else {
        retryCount++;
        if (retryCount <= CONFIG.maxRetries) {
          log(`PostHog not found. Retrying in ${CONFIG.retryDelay}ms... (Attempt ${retryCount})`);
          setTimeout(checkPostHog, CONFIG.retryDelay);
        } else {
          log('PostHog not found after maximum retries. Tracking disabled.');
        }
      }
    };
    checkPostHog();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
